import { createSlice } from "@reduxjs/toolkit";
import {
  filterExaminerExamInstances,
  getExaminerExamInstances,
  getExaminerScheduleExamInstances,
} from "../apiActions/examinerActions";
import { Exam } from "./examSlice";
import { Atc } from "./atcSlice";

type ExamInstanceResult = {
  id: string;
  created_at: string;
  status: string;
  score: number;
  total_score: number;
  percentage: number;
  category_vise_score: any;
};

type User = {
  id: string;
  created_at: string;
  username: string;
  email: string;
  permissions: string[];
  student_id: string;
  atc: Atc;
  banned: boolean;
  hidden: boolean;
};

export type ExamSession = {
  id: string;
  created_at: string;
  exam_instance_result: ExamInstanceResult;
  atc: Atc;
  user: User;
  exam: Exam;
  time: number;
  end_time: number;
  proctor_connect_url: string;
  status: string;
  available_reschedule_count: number;
  /* Provisioning state of this session's lab, as last written by the StartExamLabs
     cron: "available", "provisioning", or absent when nothing has been created yet.
     Only meaningful for papers with exam.lab_available — see labReadiness.ts. */
  lab_status?: string;
};

interface InitialState {
  loading: {
    get: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  deleteLoading: boolean;
  error: null | string;
  examinerInstances: ExamSession[];
  modal: boolean;
}

const initialState: InitialState = {
  examinerInstances: [],
  error: null,
  loading: {
    get: false,
    update: false,
    delete: false,
    create: false,
  },
  deleteLoading: false,
  modal: false,
};

const examinerSlice = createSlice({
  name: "request",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getExaminerExamInstances.pending, (state: InitialState) => {
        state.loading.get = true;
        state.error = null;
      })
      .addCase(
        getExaminerExamInstances.fulfilled,
        (state: InitialState, action) => {
          state.loading.get = false;
          state.error = null;
          state.examinerInstances = action.payload;
          state.modal = false;
        }
      )
      .addCase(
        getExaminerExamInstances.rejected,
        (state: InitialState, action) => {
          state.loading.get = false;
        }
      )
      .addCase(filterExaminerExamInstances.pending, (state: InitialState) => {
        state.loading.get = true;
        state.error = null;
      })
      .addCase(
        filterExaminerExamInstances.fulfilled,
        (state: InitialState, action) => {
          state.loading.get = false;
          state.error = null;
          state.examinerInstances = action.payload;
          state.modal = false;
        }
      )
      .addCase(
        filterExaminerExamInstances.rejected,
        (state: InitialState, action) => {
          state.loading.get = false;
        }
      )
      .addCase(
        getExaminerScheduleExamInstances.pending,
        (state: InitialState, action) => {
          /* A background re-poll must not blank the table it is refreshing. */
          if (!action.meta.arg?.background) state.loading.get = true;
          state.error = null;
        }
      )
      .addCase(
        getExaminerScheduleExamInstances.fulfilled,
        (state: InitialState, action) => {
          state.loading.get = false;
          state.error = null;
          state.examinerInstances = action.payload;
          state.modal = false;
        }
      )
      .addCase(
        getExaminerScheduleExamInstances.rejected,
        (state: InitialState, action) => {
          state.loading.get = false;
        }
      );
  },
});

export default examinerSlice.reducer;
